import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Avatar, CircularProgress,
  Alert, Chip, Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Nfc as NfcIcon,
  CardGiftcard as RewardIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TopIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Dashboard {
  periodo: string;
  funcionarios: { total: number; ativos: number; com_bracelet: number; budget_mensal: number };
  distribuicao_mes: { total: number; distribuido: number; falhas: number };
  top_spenders: { nome: string; departamento: string; total_gasto: number }[];
}

const BRAND = '#6233A0';

const fmt = (v: number) => v.toLocaleString('pt-CV', { minimumFractionDigits: 0 });

const CorporatePortalDashboard: React.FC = () => {
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/portal/corporate/dashboard')
      .then(r => setData(r.data.data))
      .catch(() => setError('Empresa não associada ao Corporate NFC ou sem permissão.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  if (error) return (
    <Box p={3}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  const d = data!;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Avatar sx={{ bgcolor: BRAND, width: 48, height: 48 }}><NfcIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Portal Empresarial</Typography>
          <Typography variant="body2" color="text.secondary">
            Corporate NFC — {d.periodo}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* KPIs */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Funcionários activos', value: d.funcionarios.ativos,    icon: <PeopleIcon />,   color: BRAND },
          { label: 'Com bracelet NFC',     value: d.funcionarios.com_bracelet, icon: <NfcIcon />,    color: '#10B981' },
          { label: 'Budget mensal (ECV)',  value: fmt(d.funcionarios.budget_mensal), icon: <RewardIcon />, color: '#F59E0B', isText: true },
          { label: 'Distribuído este mês (ECV)', value: fmt(d.distribuicao_mes.distribuido), icon: <TrendingUpIcon />, color: '#0D7F9F', isText: true },
        ].map(s => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color, mb: 1, width: 36, height: 36 }}>
                {s.icon}
              </Avatar>
              <Typography variant="h5" fontWeight={700}>
                {(s as any).isText ? s.value : s.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Distribuição do mês */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Distribuição de {d.periodo}</Typography>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box>
            <Typography variant="h4" fontWeight={700} color={BRAND}>{d.distribuicao_mes.total}</Typography>
            <Typography variant="caption" color="text.secondary">Distribuições processadas</Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#10B981">{fmt(d.distribuicao_mes.distribuido)} ECV</Typography>
            <Typography variant="caption" color="text.secondary">Total creditado</Typography>
          </Box>
          {d.distribuicao_mes.falhas > 0 && (
            <Box>
              <Typography variant="h4" fontWeight={700} color="error.main">{d.distribuicao_mes.falhas}</Typography>
              <Typography variant="caption" color="text.secondary">Falhas</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Top Spenders */}
      {d.top_spenders?.length > 0 && (
        <Paper sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TopIcon sx={{ color: '#F59E0B' }} />
            <Typography variant="h6" fontWeight={700}>Top Utilizadores este mês</Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            {d.top_spenders.map((s, i) => (
              <Box key={s.nome} display="flex" alignItems="center" justifyContent="space-between"
                sx={{ p: 1.5, borderRadius: 1, bgcolor: i === 0 ? 'rgba(245, 158, 11, 0.12)' : 'action.hover' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: i === 0 ? '#F59E0B' : 'grey.300', fontSize: 13 }}>
                    {i + 1}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{s.nome}</Typography>
                    {s.departamento && (
                      <Typography variant="caption" color="text.secondary">{s.departamento}</Typography>
                    )}
                  </Box>
                </Box>
                <Chip label={`${fmt(s.total_gasto)} ECV`} size="small"
                  sx={{ bgcolor: i === 0 ? 'rgba(245, 158, 11, 0.12)' : 'transparent', fontWeight: 700 }} />
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default CorporatePortalDashboard;
